import { useEffect } from "react";

import { Divider } from "@vericus/cadmus-ui";

import { useAppDispatch } from "@/data/hooks";
import { updateSocraticSessionTimeline } from "@/features/socratic";
import { useRecordingStartCutoff } from "@/features/socratic/hooks";
import { useSocraticSessionQuery } from "@/generated/graphql";

import { hasCompletedResponse } from "../../helpers/has-dialogue-response";
import {
  ResponseRequirements,
  ResponseRequirementsProps,
} from "./response-requirements";
import { SocraticAttemptActions } from "./socratic-attempt-actions";
import * as styles from "./socratic-session-entry.css";
import { StartDialogueCTASkeleton } from "./start-dialogue-cta";

interface Props {
  answerBlockId: string;
  workId: string;
  responseRequirements: Omit<
    ResponseRequirementsProps,
    "studentMaxAttempts" | "hasSubmittedResponse"
  >;
  isReadonly?: boolean;
}

/**
 * Component manages attempt state (start / re-attempt) for a Socratic
 * interaction per AnswerBlock.
 */
export function SocraticSessionEntry(props: Props) {
  const dispatch = useAppDispatch();

  const { data, loading } = useSocraticSessionQuery({
    variables: {
      answerBlockId: props.answerBlockId,
      workId: props.workId,
    },
  });
  const session = data?.socraticSession;

  const recordingStartCutoff = useRecordingStartCutoff(props.answerBlockId);

  useEffect(() => {
    if (!session) return;
    dispatch(
      updateSocraticSessionTimeline({
        reattemptWindowEndsAt: session.reattemptWindowEndsAt,
        recordingStartCutoffAt: recordingStartCutoff?.toISOString() ?? null,
      })
    );
  }, [dispatch, session, recordingStartCutoff]);

  if (loading || !session) return <StartDialogueCTASkeleton />;

  return (
    <div className={styles.root}>
      <ResponseRequirements
        {...props.responseRequirements}
        studentMaxAttempts={session.maxAttempts}
        hasSubmittedResponse={hasCompletedResponse(session.dialogues)}
      />
      <Divider />
      <SocraticAttemptActions
        session={session}
        workId={props.workId}
        answerBlockId={props.answerBlockId}
        isReadonly={props.isReadonly}
        hasPreparationTime={
          (props.responseRequirements.preparationTimeSeconds ?? 0) > 0
        }
      />
    </div>
  );
}
